import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";
import { makeWorld, floor, walls, box, ball, nGon, trapezoid } from "./helpers";

/**
 * A neat grid of every supported shape — circles, boxes, triangles, pentagons,
 * hexagons, octagons and trapezoids — dropped at once so they tumble and settle
 * into a heap. The quickest visual proof that Fast2D handles arbitrary convex
 * polygons, not just circles and boxes.
 */
export const shapes: Demo = {
	id: "shapes",
	title: "Shape zoo",
	blurb:
		"Circles, boxes, triangles, pentagons, hexagons, octagons and trapezoids drop together and settle into a heap — convex polygons of every flavor colliding at once.",
	category: "Stacking & Stress",
	build(): Scene {
		const world = makeWorld();
		floor(world, 20, 2);
		walls(world, 8, 18);

		const cols = 7;
		const rows = 5;
		const spacing = 1.7;
		let idx = 0;
		for (let row = 0; row < rows; row++) {
			for (let col = 0; col < cols; col++) {
				const x = (col - (cols - 1) / 2) * spacing;
				const y = -2 - row * spacing;
				const r = 0.62;
				const opts = { friction: 0.5, restitution: 0.05 };
				switch (idx % 7) {
					case 0:
						ball(world, x, y, r, { ...opts, restitution: 0.12 });
						break;
					case 1:
						nGon(world, x, y, 3, r + 0.18, opts); // triangle
						break;
					case 2:
						box(world, x, y, r * 1.7, r * 1.7, opts); // square
						break;
					case 3:
						nGon(world, x, y, 5, r + 0.05, opts); // pentagon
						break;
					case 4:
						nGon(world, x, y, 6, r + 0.05, opts); // hexagon
						break;
					case 5:
						nGon(world, x, y, 8, r + 0.05, opts); // octagon
						break;
					default:
						trapezoid(world, x, y, r * 2.2, r * 1.5, 0.45, opts);
						break;
				}
				idx++;
			}
		}

		return {
			world,
			camera: new Camera(0, -5, 22),
			hint: "Every shape is a real convex collider. Grab one and toss it through the pile.",
		};
	},
};
